import { useMutation } from "@apollo/client/react"
import LoadingButton from "@mui/lab/LoadingButton"
import { Alert } from "@mui/material"
import { DateTime } from "luxon"
import React, { useEffect, useMemo, useState } from "react"

import { PublicResendReservationConfirmationDocument, type PublicCreateReservationMutation } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

import confirmationIcon from "./reservation_confirmation_icon.svg"

type ReservationConfirmationStepProps = {
  reservation: NonNullable<PublicCreateReservationMutation["publicCreateReservation"]>["reservation"]
  confirmUntil?: string | null
  resendAvailableAt?: string | null
  resendsRemaining: number
  onClose: () => void
  reservationToken?: string
  customerEmail?: string
}

const CONFIRMATION_ICON = (
  <div className="relative h-[70px] w-[73px]" aria-hidden>
    <img src={confirmationIcon} alt="" className="block h-full w-full" />
  </div>
)

const formatRemainingTime = (value: string | null | undefined) => {
  if (!value) return null

  const diffInSeconds = Math.max(0, Math.ceil(DateTime.fromISO(value).diffNow("seconds").seconds))
  const minutes = Math.floor(diffInSeconds / 60)
  const seconds = diffInSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

const ReservationConfirmationStep: React.FC<ReservationConfirmationStepProps> = ({
  reservation,
  confirmUntil,
  resendAvailableAt,
  resendsRemaining,
  onClose,
  reservationToken = "",
  customerEmail,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [, setNow] = useState(() => DateTime.now())

  const [resendConfirmation, { loading: isResending, data: resendData }] = useMutation(
    PublicResendReservationConfirmationDocument,
  )

  const currentConfirmUntil = resendData?.publicResendReservationConfirmation?.confirmUntil ?? confirmUntil
  const currentResendAvailableAt =
    resendData?.publicResendReservationConfirmation?.resendAvailableAt ?? resendAvailableAt
  const currentResendsRemaining = resendData?.publicResendReservationConfirmation?.resendsRemaining ?? resendsRemaining

  useEffect(() => {
    const timer = window.setInterval(() => setNow(DateTime.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const cooldownActive = useMemo(() => {
    if (!currentResendAvailableAt) return false
    return DateTime.fromISO(currentResendAvailableAt) > DateTime.now()
  }, [currentResendAvailableAt])

  const expiresIn = formatRemainingTime(currentConfirmUntil)
  const resendIn = formatRemainingTime(currentResendAvailableAt)

  const mapErrorCode = (code?: string | null) => {
    if (code === "TOO_EARLY") return i18n.t("app.public.venue.modal.resend_too_early")
    if (code === "TOO_MANY_ATTEMPTS") return i18n.t("app.public.venue.modal.resend_limit_reached")
    if (code === "NOT_CONFIRMABLE") return i18n.t("app.public.venue.modal.resend_not_confirmable")

    return i18n.t("app.public.new_reservation.errors.generic")
  }

  const handleResend = async () => {
    setError(null)

    try {
      const result = await resendConfirmation({
        variables: {
          reservationId: reservation?.id,
          token: reservationToken || undefined,
          email: reservationToken ? undefined : customerEmail,
        },
      })

      const errors = result.data?.publicResendReservationConfirmation?.errors || []

      if (errors.length > 0) {
        setError(mapErrorCode(errors[0]?.code))
      }
    } catch (resendError) {
      showErrorToast()
      console.error("Error resending confirmation email:", resendError)
      setError(i18n.t("app.public.new_reservation.errors.generic"))
    }
  }

  return (
    <div
      className="mx-auto flex h-full w-full max-w-[500px] flex-col bg-white"
      data-testid="public-reservation-modal-confirmation-container"
    >
      <div
        className="flex min-h-[220px] flex-col items-center gap-5 px-5 pb-10 pt-5 text-center sm:h-[220px]"
        data-testid="public-reservation-modal-confirmation-hero"
      >
        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-[#f5f7f9] text-[22px] leading-none text-[#8d8d8d]"
            data-testid="public-reservation-modal-confirmation-close"
            aria-label={i18n.t("app.public.venue.modal.close")}
          >
            ×
          </button>
        </div>

        {CONFIRMATION_ICON}

        <h2 className="m-0 text-[22px] font-medium tracking-[-0.5px] text-[#201f1f]">
          {i18n.t("app.public.venue.modal.check_your_email")}
        </h2>
      </div>

      <div className="flex flex-col items-center gap-[30px] px-5 text-center">
        <p className="m-0 max-w-[360px] text-[14px] leading-5 text-[#3e3e3e]">
          {i18n.t("app.public.venue.modal.check_your_email_description")}
        </p>

        {!!expiresIn && (
          <div className="flex h-8 w-full max-w-[400px] items-center justify-center rounded-[5px] bg-[#f5f7f9] px-4 text-[12px] text-[#3e3e3e]">
            {i18n.t("app.public.venue.modal.confirmation_expires_in", { time: expiresIn })}
          </div>
        )}
      </div>

      <div className="bg-white p-5">
        {!!error && (
          <Alert severity="error" onClose={() => setError(null)} className="mb-5 w-full text-left">
            {error}
          </Alert>
        )}

        <LoadingButton
          variant="contained"
          onClick={onClose}
          fullWidth
          size="large"
          className="w-full"
          sx={{
            backgroundColor: "#2596fa",
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            minHeight: "40px",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#1e7cd8", boxShadow: "none" },
          }}
          data-testid="public-reservation-modal-confirmation-primary-btn"
        >
          {i18n.t("app.public.venue.modal.go_to_my_email")}
        </LoadingButton>
      </div>

      <div className="bg-[#f5f7f9] px-5 py-5 text-center text-[10px] text-[#979797] sm:rounded-b-[10px]">
        <div className="mx-auto max-w-[280px] leading-[normal]">
          {cooldownActive ? (
            <span data-testid="public-reservation-modal-confirmation-resend-cooldown">
              {i18n.t("app.public.venue.modal.resend_available_in", { time: resendIn || "0:00" })}
            </span>
          ) : currentResendsRemaining > 0 ? (
            <span>
              {i18n.t("app.public.venue.modal.didnt_get_the_email")}{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className="border-0 bg-transparent p-0 text-[10px] font-semibold text-[#2596fa] underline disabled:text-[#8dbff0]"
                data-testid="public-reservation-modal-confirmation-resend-btn"
              >
                {i18n.t("app.public.venue.modal.send_it_again")}
              </button>
            </span>
          ) : (
            <span data-testid="public-reservation-modal-confirmation-resend-limit">
              {i18n.t("app.public.venue.modal.resend_limit_reached")}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReservationConfirmationStep
